<template>
  <div class="container" @click="goToHome">
    <div v-if="errorMessage" class="content">
      <p class="no-content-message">{{ errorMessage }}</p>
    </div>
    <div v-else-if="currentFile" class="media-container">
      <video
        v-if="isVideo"
        :key="currentFile"
        :src="currentFile"
        ref="screensaverVideo"
        class="fullscreen-video"
        autoplay
        muted
        preload="auto"
        playsinline
        @ended="nextFile"
        @error="handleMediaError"
      >
        Votre navigateur ne supporte pas les vidéos HTML5.
      </video>
      <img v-else :key="currentFile" class="fullscreen-image" :src="currentFile" @load="startImageTimer" @error="handleMediaError" />
    </div>
    <div v-else-if="!isLoading" class="content">
      <p class="no-content-message">Il n'y a pas de contenu à afficher dans {{ contentUrl }}</p>
    </div>
    <div class="footer">
      Made by Des Choses Pareilles - Version {{ $store.state.packageVersion }} {{ queryParams }}
    </div>
  </div>
</template>

<script>
import domains from '@/assets/data/domains.js';
import { resolveMediaUrl } from '@/utils/mediaUrl.js';

export default {
  data() {
    return {
      files: [],
      originalFiles: [],
      currentIndex: 0,
      contentUrl: '',
      errorMessage: '',
      imageTimer: null,
      isLoading: true,
    };
  },
  computed: {
    currentFile() {
      if (!this.contentUrl || this.files.length === 0) return null;

      return this.files[this.currentIndex];
    },
    isVideo() {
      return this.currentFile && new URL(this.currentFile).pathname.toLowerCase().endsWith('.mp4');
    }
  },
  mounted() {
    console.log('Component mounted');

    const unitName = this.$route?.query?.unitName;
    console.log('unitName from URL =', unitName);

    if (!unitName) {
      this.errorMessage = `Paramètre "unitName" manquant dans l'URL`;
      console.warn(this.errorMessage);
      return;
    }

    console.log('unitname in url');

    if (typeof unitName !== 'string' || !unitName.trim() || !domains[unitName]) {
      this.errorMessage = `No domain for unitName = ${unitName || '[none]'}`;
      console.warn(this.errorMessage);
      return;
    }

    const domain = domains[unitName];
    this.contentUrl = `https://netplus.choses.ch/content/${domain}/`;
    console.log('Resolved contentUrl =', this.contentUrl);

    this.fetchFiles();
  },
  beforeUnmount() {
    this.clearImageTimer();
  },
  methods: {
    handleMediaError() {
      this.clearImageTimer();
      const originalFile = this.originalFiles[this.currentIndex];
      if (originalFile && this.currentFile !== originalFile) {
        // Retry the original sources once; changing the key recreates the player.
        // No timeout: a working proxy may need to download the entire file first.
        console.warn('Cache kiosque indisponible pour ce média : lecture directe depuis Netplus.');
        this.files = [...this.originalFiles];
        return;
      }
      this.errorMessage = 'Impossible de charger le média. Vérifiez votre connexion et la disponibilité du fichier.';
    },
    async fetchFiles() {
      try {
        const response = await fetch(this.contentUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");
        const links = Array.from(doc.querySelectorAll("a"))
          .map((a) => a.getAttribute('href'))
          .filter(Boolean)
          .map((href) => new URL(href, this.contentUrl))
          .filter((url) => /\.(mp4|jpg|png|jpeg)$/i.test(url.pathname))
          .sort((a, b) => a.pathname.localeCompare(b.pathname, undefined, { numeric: true, sensitivity: 'base' }))
          .map((url) => url.href);

        this.originalFiles = links;
        this.files = links.map((url) => resolveMediaUrl(url));

        if (this.files.length === 0) {
          console.warn("Aucun fichier trouvé.");
        } else {
          console.log(this.files);
          this.startSlideshow();
        }
      } catch (error) {
        this.errorMessage = error.message;
        console.error("Erreur lors de la récupération des fichiers :", error);
      } finally {
        this.isLoading = false;
      }
    },
    startSlideshow() {
      if (this.files.length > 0) {
        const indexFromUrl = parseInt(this.$route.query.index, 10);

        if (!Number.isNaN(indexFromUrl) && indexFromUrl >= 0 && indexFromUrl < this.files.length) {
          this.currentIndex = indexFromUrl;
        } else {
          this.currentIndex = 0;
        }
      }
    },
    nextFile() {
      this.clearImageTimer();

      if (this.files.length <= 1) {
        this.restartCurrentVideo();
        return;
      }

      const nextIndex = (this.currentIndex + 1) % this.files.length;
      this.currentIndex = nextIndex;
      // Keep the URL resumable without reloading the application or its listing.
      this.$router.replace({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          index: nextIndex,
        },
      });
    },

    restartCurrentVideo() {
      this.$nextTick(() => {
        const video = this.$refs.screensaverVideo;

        if (!video) return;

        video.currentTime = 0;
        video.play();
      });
    },

    startImageTimer() {
      this.clearImageTimer();

      this.imageTimer = setTimeout(this.nextFile, 5000);
    },

    clearImageTimer() {
      if (!this.imageTimer) return;

      clearTimeout(this.imageTimer);
      this.imageTimer = null;
    },

    goToHome() {
      this.$router.push({ name: "home" });
    }
  }
};
</script>

<style lang="scss" scoped>
.container {
  background: $secondary-color;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.media-container {
  background: #000;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.fullscreen-video,
.fullscreen-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-content-message {
  font-size: 1.5rem;
  color: black;
  text-align: center;
  padding: 20px;
}
</style>
